import { FC, PropsWithChildren, useEffect, useState } from "react";
import { Order } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { deleteOrder } from "@/services/Orders";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mantine/core";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import CreateOrderCta from "../../../molecules/CreateOrderCta";
import OrderCard from "./OrderCard";
import OrderList from "./OrderList";
import ViewToggle from "../ViewToggle";
import Stack from "@/components/atoms/Stack";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import styles from "./styles.module.css";

interface Props {
  createNew: () => void;
  handleActiveOrder: (order: Order) => void;
  orders: Order[];
  isLoadingOrders: boolean;
  pageNumber: number;
  limit: number;
  totalOrders: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (value: number) => void;
}

const ActiveOrders: FC<Props> = ({
  createNew,
  handleActiveOrder,
  orders,
  isLoadingOrders,
  pageNumber,
  limit,
  totalOrders,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const { t } = useTranslation();
  const { currentCompany, isViewer } = useAuth();
  const queryClient = useQueryClient();

  const [showListAsGrid, setShowListAsGrid] = useState(true);
  const [deleteOrderConfig, setDeleteOrderConfig] = useState({
    isOpen: false,
    orderId: null as number | null,
  });

  useEffect(() => {
    if (orders.length > 11) setShowListAsGrid(false);
  }, [orders.length]);

  const { mutateAsync: mutateDeleteOrder } = useMutation({
    mutationFn: async (orderId: number) => {
      await deleteOrder(orderId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOrdersForCompany", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.orderDeleted"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.orderDeletedError"))
      );
    },
  });

  const closeCancelConfirmation = () => {
    setDeleteOrderConfig({ isOpen: false, orderId: null });
  };

  const cancelOrder = async () => {
    if (deleteOrderConfig.orderId) {
      await mutateDeleteOrder(deleteOrderConfig.orderId);
    }
    closeCancelConfirmation();
  };

  const openDeleteOrderModal = (orderId: number) => {
    setDeleteOrderConfig({ isOpen: true, orderId });
  };

  return (
    <>
      <Modal opened={deleteOrderConfig.isOpen} onClose={() => closeCancelConfirmation()} title={t("actions:confirm")}>
        <Stack p="xl">
          <Text>{t("orders:cancelTextConfirmation")}</Text>
        </Stack>
        <ModalCTAs>
          <CTA id="delete-order-cancel" intent="secondary" onClick={() => closeCancelConfirmation()}>
            {t("actions:cancel")}
          </CTA>
          <CTA id="delete-order-confirm" intent="primary" onClick={() => cancelOrder()}>
            {t("actions:accept")}
          </CTA>
        </ModalCTAs>
      </Modal>

      {showListAsGrid ? (
        <Wrap>
          <div className={styles.viewToggleBar}>
            <ViewToggle showListAsGrid={showListAsGrid} setShowListAsGrid={setShowListAsGrid} />
          </div>
          <div className={styles.inner}>
            {!isViewer(currentCompany) && <CreateOrderCta onClick={createNew} />}
            {isLoadingOrders
              ? Array(3)
                  .fill(null)
                  .map((_, i) => <SkeletonOrderCard key={i} />)
              : orders.map((order: any, i: number) => (
                  <OrderCard
                    order={order}
                    key={"active-order-box-" + i}
                    handleActiveOrder={handleActiveOrder}
                    deleteOrder={(orderId) => openDeleteOrderModal(orderId)}
                  />
                ))}
          </div>
        </Wrap>
      ) : (
        <OrderList
          orders={orders}
          showListAsGrid={showListAsGrid}
          setShowListAsGrid={setShowListAsGrid}
          handleActiveOrder={handleActiveOrder}
          deleteOrder={(orderId) => openDeleteOrderModal(orderId)}
          pageNumber={pageNumber}
          limit={limit}
          totalOrders={totalOrders}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </>
  );
};

const SkeletonOrderCard: FC = () => (
  <div className={styles.skeletonCard}>
    <Stack gap={8}>
      <Skeleton height={22} width="70%" />
      <Skeleton height={16} width="50%" />
    </Stack>
    <Stack gap={8} mt={16}>
      <Skeleton height={12} width="40%" />
      <Skeleton height={18} width="60%" />
      <Skeleton height={12} width="40%" />
      <Skeleton height={18} width="60%" />
    </Stack>
    <Group mt={16} grow>
      <Skeleton height={36} radius="sm" />
      <Skeleton height={36} radius="sm" />
    </Group>
  </div>
);

const Wrap: FC<PropsWithChildren> = (props) => {
  return <div className={styles.activeOrdersRoot}>{props.children}</div>;
};

export default ActiveOrders;
